//landing-page
import React, { useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery
} from '@mui/material';
import { styled, useTheme } from '@mui/system';

// コンポーネントのインポート
import IssueTabs from '../lp/issuesTab';
import Features from '../lp/Features';
import LogoSlider from '../lp/LogoSlider'; // LogoSliderをインポート
import Summary from '../lp/Summary'; // Summaryをインポート
import Contact from '../lp/Contact'; // Contactをインポート

// アイコンのインポート
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import BatchPredictionIcon from '@mui/icons-material/BatchPrediction';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SendIcon from '@mui/icons-material/Send';

const HeroSection = styled(Box)(({ theme }) => ({
  height: '100vh',
  width: '100vw',
  overflow: 'hidden',
  backgroundColor: '#f0f4f8',
  padding: 0,
  textAlign: 'center',
  backgroundImage: 'url(/images/Hero_bg.png)',
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  scrollSnapAlign: 'start', // スクロールスナップを再度追加
}));

const HeroTextBox = styled(Box)(({ theme }) => ({
  padding: '0% 10%',
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}));

const HeroImageBox = styled(Box)(({ theme }) => ({
  height: '100%',
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
}));

const FeaturesSection = styled(Box)(({ theme }) => ({
  height: '100vh',
  width: '100vw',
  overflow: 'hidden',
  textAlign: 'center',
  backgroundImage: 'url(/images/Features_bg.svg)',
  scrollSnapAlign: 'start', // スクロールスナップを再度追加
}));

const IssueSection = styled(Box)(({ theme }) => ({
  height: '100vh',
  width: '100vw',
  overflow: 'hidden',
  backgroundColor: '#f0f4f8',
  padding: '0',
  textAlign: 'center',
  display: 'flex',
  scrollSnapAlign: 'start', // スクロールスナップを再度追加
}));

const LogoSection = styled(Box)(({ theme }) => ({
  height: '100vh',
  width: '100vw',
  overflow: 'hidden',
  textAlign: 'center',
  backgroundColor: '#ebebeb',
  padding: '0',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  scrollSnapAlign: 'start', // スクロールスナップを再度追加
}));

const SummarySection = styled(Box)(({ theme }) => ({
  height: '100vh',
  width: '100vw',
  overflow: 'hidden',
  backgroundColor: '#ffffff', // 背景色を白に設定
  padding: '0',
  textAlign: 'center',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundImage: 'url(/images/Summary_bg.svg)', // 背景画像を設定
  scrollSnapAlign: 'start', // スクロールスナップを再度追加
}));

const ContactSection = styled(Box)(({ theme }) => ({
  height: '100vh',
  width: '100vw',
  overflow: 'hidden',
  backgroundColor: '#f0f4f8',
  padding: '0',
  textAlign: 'center',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  scrollSnapAlign: 'start', // スクロールスナップを再度追加
}));

const NextiLandingPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // モバイルデバイスの判定
  const [value, setValue] = React.useState(0);
  const [alignment, setAlignment] = React.useState('hero-section'); // ナビゲーションボタンの状態管理

  // 各セクションへの参照を作成
  const heroSectionRef = useRef<HTMLDivElement>(null);
  const featuresSectionRef = useRef<HTMLDivElement>(null);
  const issueSectionRef = useRef<HTMLDivElement>(null);
  const logoSectionRef = useRef<HTMLDivElement>(null);
  const summarySectionRef = useRef<HTMLDivElement>(null);
  const contactSectionRef = useRef<HTMLDivElement>(null);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleAlignment = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string,
  ) => {
    if (newAlignment !== null) {
      // 対応するセクションにスクロール
      document.getElementById(newAlignment)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const observerOptions = {
      root: null, // ビューポートをルートとする
      rootMargin: '0px',
      threshold: 0.75, // セクションの75%が見えたらコールバックを実行
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // 現在表示されているセクションのIDに基づいてalignmentを更新
          setAlignment(entry.target.id);
        }
      });
    }, observerOptions);

    // 各セクションを監視対象に追加
    if (heroSectionRef.current) observer.observe(heroSectionRef.current);
    if (featuresSectionRef.current) observer.observe(featuresSectionRef.current);
    if (issueSectionRef.current) observer.observe(issueSectionRef.current);
    if (logoSectionRef.current) observer.observe(logoSectionRef.current);
    if (summarySectionRef.current) observer.observe(summarySectionRef.current);
    if (contactSectionRef.current) observer.observe(contactSectionRef.current);

    // クリーンアップ関数
    return () => {
      if (heroSectionRef.current) observer.unobserve(heroSectionRef.current);
      if (featuresSectionRef.current) observer.unobserve(featuresSectionRef.current);
      if (issueSectionRef.current) observer.unobserve(issueSectionRef.current);
      if (logoSectionRef.current) observer.unobserve(logoSectionRef.current);
      if (summarySectionRef.current) observer.unobserve(summarySectionRef.current);
      if (contactSectionRef.current) observer.unobserve(contactSectionRef.current);
    };
  }, []); // 空の依存配列でマウント時に一度だけ実行

  return (
    <main style={{ overflowY: 'scroll', scrollSnapType: 'y mandatory', height: '100vh' }}> {/* CSSスクロールスナップを適用 */}
      <Helmet>
        <title>インフォメーションハブ | 次世代のカスタマーサービス</title>
        <meta name="description" content="次世代のカスタマーサービス「インフォメーションハブ」を導入しませんか？ Web集客、システム開発でお客様の事業をサポートします。" />
        <meta property="og:title" content="インフォメーションハブ | 次世代のカスタマーサービス" />
        <meta property="og:description" content="次世代のカスタマーサービス「インフォメーションハブ」を導入しませんか？ Web集客、システム開発でお客様の事業をサポートします。" />
        <meta property="og:url" content="https://ludyworks.com/nexti_landingpage" />
        <meta property="og:image" content="https://ludyworks.com/images/device.png" />
      </Helmet>
      <ToggleButtonGroup
        orientation="vertical"
        value={alignment}
        exclusive
        onChange={handleAlignment}
        aria-label="text alignment"
        sx={{
          position: 'fixed',
          right: 20,
          top: { xs: '80%', md: '50%' }, // モバイルでは中央、デスクトップでは上部に配置
          transform: 'translateY(-50%)',
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          borderRadius: '10px',
          boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
          '.Mui-selected': {
            backgroundColor: '#1976d2 !important', // 選択されたボタンの背景色
            color: '#fff !important', // 選択されたボタンの文字色
          }
        }}
      >
        <ToggleButton value="hero-section" aria-label="hero section">
          <VerticalAlignTopIcon />
        </ToggleButton>
        <ToggleButton value="features-section" aria-label="features section">
          <ViewCarouselIcon />
        </ToggleButton>
        <ToggleButton value="issue-section" aria-label="issue section">
          <BatchPredictionIcon />
        </ToggleButton>
        <ToggleButton value="logo-section" aria-label="logo section">
          <SupervisorAccountIcon />
        </ToggleButton>
        <ToggleButton value="summary-section" aria-label="summary section"> {/* SummarySectionのナビゲーションボタンを追加 */}
          <SummarizeIcon />
        </ToggleButton>
        <ToggleButton value="contact-section" aria-label="contact section">
          <SendIcon />
        </ToggleButton>
      </ToggleButtonGroup>

      <HeroSection 
        id="hero-section" 
        ref={heroSectionRef}
        sx={{ flexDirection: {xs: 'column', md: 'row'} }}
      >
        <HeroTextBox>
          <Typography variant="h2" component="h1" gutterBottom textAlign={'left'} fontWeight={'500'}>
            つながる<br />
            ひろがる<br />
            思い出になる
          </Typography>
          <Typography 
            variant="h5" 
            component="p" 
            color="text.secondary" 
            gutterBottom
          >
            次世代のカスタマーサービス「インフォメーションハブ」を導入しませんか？
          </Typography>
        </HeroTextBox>
        <HeroImageBox>
          <img width={'100%'} src="/images/device.png" alt="Background" />
        </HeroImageBox>
      </HeroSection>
      <Divider />
      <FeaturesSection
        id="features-section" 
        ref={featuresSectionRef}
      > {/* idとrefを追加 */}
        <Features />
      </FeaturesSection>
      <Divider />
      <IssueSection id="issue-section" ref={issueSectionRef}> {/* idとrefを追加 */}
        <IssueTabs />
      </IssueSection>
      <Divider />
      <LogoSection id="logo-section" ref={logoSectionRef}> {/* idとrefを追加 */}
        <LogoSlider />
      </LogoSection>
      <Divider />
      <SummarySection id="summary-section" ref={summarySectionRef}> {/* idとrefを渡す */}
        <Summary /> {/* Summaryコンポーネントを使用 */}
      </SummarySection>
      <Divider />
      <ContactSection sx={{ height: isMobile ? '150vh' : '100vh' }} id="contact-section" ref={contactSectionRef}> {/* idとrefを追加 */}
        <Contact /> {/* Contactコンポーネントを使用 */}
      </ContactSection>
    </main>
  );
};

export default NextiLandingPage;
